import { useEffect } from 'react';

/**
 * Freeze the page behind a full-screen overlay.
 *
 * Every overlay that covers the page needs this — without it the page keeps
 * scrolling under the blurred backdrop, which reads as a broken modal (and on
 * a phone the content visibly slides behind the sheet you are typing into).
 *
 * Locks are REFERENCE-COUNTED at module level rather than each overlay saving
 * and restoring `body.style` itself: with per-overlay save/restore two
 * simultaneous overlays can unlock in the wrong order (the one that opened
 * first restores the pre-lock value while the second is still open), and the
 * page starts scrolling behind a modal that is still up.
 */
let lockCount = 0;
let savedStyles: { overflow: string; paddingRight: string } | null = null;

function applyLock(): void {
  const body = document.body;
  savedStyles = {
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
  };
  // Compensate for the disappearing vertical scrollbar so the page doesn't
  // jump horizontally when the overlay opens. Overlay scrollbars (iOS,
  // Android) measure 0 here, so this is a no-op on touch devices.
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    const currentPad = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${currentPad + scrollbarWidth}px`;
  }
}

function releaseLock(): void {
  if (!savedStyles) return;
  document.body.style.overflow = savedStyles.overflow;
  document.body.style.paddingRight = savedStyles.paddingRight;
  savedStyles = null;
}

/**
 * @param active `false` opts out (e.g. an overlay that stays mounted while
 *        hidden), so callers don't have to branch around the hook.
 */
export function useBodyScrollLock(active = true): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    if (lockCount === 0) applyLock();
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) releaseLock();
    };
  }, [active]);
}
